import React, { useState, useCallback } from 'react';
import { 
  Upload, 
  FileText, 
  Database, 
  Box, 
  Factory,
  X,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  BarChart3
} from 'lucide-react';
import { API_URL } from '../../../utils/constant';

const ImportPage = () => {
  // Main tab: default to sealed
  const [mainTab, setMainTab] = useState('sealed');
  
  // File type selection
  const [selectedFileType, setSelectedFileType] = useState(null);
  
  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [error, setError] = useState(null);
  const [showRawData, setShowRawData] = useState(false);
  const [stockOption, setStockOption] = useState('current_retail');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [unmatchedList, setUnmatchedList] = useState([]);
  const [selectedUnmatched, setSelectedUnmatched] = useState({});
  const [createCategory, setCreateCategory] = useState('1');
  const [isCreatingUnmatched, setIsCreatingUnmatched] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState(null);
  // Confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false);

  const fileTypes = [
    { 
      id: 'wsi', 
      name: 'WSI File', 
      description: 'Import WSI Dilutions data',
      icon: <FileSpreadsheet size={24} />,
      color: 'primary',
      category: 'master',
      apiEndpoint: '/jnm-import/wsi'
    },
    { 
      id: 'wsg', 
      name: 'WSG File', 
      description: 'Import WSG product data',
      icon: <Database size={24} />,
      color: 'success',
      category: 'master',
      apiEndpoint: '/jnm-import/wsg'
    },
    { 
      id: 'reckweg', 
      name: 'Reckweg File', 
      description: 'Import Reckweg medicines',
      icon: <Factory size={24} />,
      color: 'purple',
      category: 'master',
      apiEndpoint: '/jnm-import/reck'
    },
    { 
      id: 'bt', 
      name: 'B&T File', 
      description: 'Import B&T product data',
      icon: <FileSpreadsheet size={24} />,
      color: 'info',
      category: 'master',
      apiEndpoint: '/jnm-import/bt'
    },
    { 
      id: 'other_products', 
      name: 'Other Products File', 
      description: 'Import SBL & other products',
      icon: <Box size={24} />,
      color: 'info',
      category: 'master',
      apiEndpoint: '/jnm-import/other-products'
    },
    { 
      id: 'jnu', 
      name: 'JNU Own File', 
      description: 'Import JNU internal data',
      icon: <Box size={24} />,
      color: 'warning',
      category: 'master',
      apiEndpoint: '/api/import/jnu'
    },
    { 
      id: 'wsi_stock', 
      name: 'WSI Stock & MRP', 
      description: 'Import WSI stock quantities & MRP prices',
      icon: <BarChart3 size={24} />,
      color: 'primary',
      category: 'stock',
      apiEndpoint: '/jnm-import/wsi-stock'
    },
    { 
      id: 'reck_stock', 
      name: 'Reckweg Stock & MRP', 
      description: 'Import Reckweg stock quantities & MRP prices',
      icon: <BarChart3 size={24} />,
      color: 'purple',
      category: 'stock',
      apiEndpoint: '/jnm-import/reck-stock'
    },
    { 
      id: 'wsg_stock', 
      name: 'WSG Stock & MRP', 
      description: 'Import WSG stock quantities & MRP prices',
      icon: <BarChart3 size={24} />,
      color: 'success',
      category: 'stock',
      apiEndpoint: '/jnm-import/wsg-stock'
    },
    { 
      id: 'bt_stock', 
      name: 'B&T Stock & MRP', 
      description: 'Import B&T stock quantities & MRP prices',
      icon: <BarChart3 size={24} />,
      color: 'info',
      category: 'stock',
      apiEndpoint: '/jnm-import/bt-stock'
    },
    { 
      id: 'hsn_update', 
      name: 'HSN Code Update', 
      description: 'Update HSN codes & GST rates for all products',
      icon: <FileText size={24} />,
      color: 'dark',
      category: 'utility',
      apiEndpoint: '/jnm-import/hsn-update'
    }
  ];

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  }, []);

  // Just store the file and show confirmation, don't process yet
  const handleFileSelect = (file) => {
    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadedFile(file);
    setError(null);
    setExtractedData(null);
    setShowRawData(false);
    setShowConfirmation(true);
  };

  // Helper to extract unmatched products list from API response
  const extractUnmatchedFromData = (data) => {
    const set = new Set();
    if (data) {
      // 1. Check top-level unmatched array (B&T, Other Products, WSI single sheet)
      if (data.unmatched && Array.isArray(data.unmatched)) {
        data.unmatched.forEach(item => { if (item) set.add(String(item).trim()); });
      }
      // 2. Check nested sheets object (WSI, Reck, WSG multi-sheet)
      if (data.sheets && typeof data.sheets === 'object') {
        Object.values(data.sheets).forEach(s => {
          if (s && s.unmatched && Array.isArray(s.unmatched)) {
            s.unmatched.forEach(item => { if (item) set.add(String(item).trim()); });
          }
          if (s && s.data && s.data.unmatched && Array.isArray(s.data.unmatched)) {
            s.data.unmatched.forEach(item => { if (item) set.add(String(item).trim()); });
          }
        });
      }
    }
    const list = Array.from(set);
    setUnmatchedList(list);

    const initialSelected = {};
    list.forEach(item => { initialSelected[item] = true; });
    setSelectedUnmatched(initialSelected);
  };

  // Process the file after confirmation
  const handleProceed = async () => {
    if (!uploadedFile) return;

    if (!stockOption) {
      setError('Please choose a stock option before proceeding.');
      return;
    }
    
    setShowConfirmation(false);
    setIsProcessing(true);
    setCreateSuccessMsg(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('type', selectedFileType);
      formData.append('mode', mainTab);
      formData.append('stockType', stockOption);
      formData.append('categoryName', selectedCategory);
      formData.append('company_id', localStorage.getItem('company_id') || '4');

      const fileTypeConfig = fileTypes.find(t => t.id === selectedFileType);
      
      // Call API endpoint
      const response = await fetch(`${API_URL}${fileTypeConfig.apiEndpoint}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setExtractedData(data);
      extractUnmatchedFromData(data);

    } catch (err) {
      setError(err.message || 'Error processing file. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle creating unmatched products in database
  const handleCreateUnmatched = async () => {
    const toCreate = unmatchedList.filter(item => selectedUnmatched[item]);
    if (toCreate.length === 0) {
      setError('Please select at least one unmatched product to create.');
      return;
    }

    setIsCreatingUnmatched(true);
    setCreateSuccessMsg(null);
    setError(null);

    try {
      // Determine brand name from selected file type
      let brandName = 'WSI';
      if (selectedFileType === 'wsg' || selectedFileType === 'wsg_stock') brandName = 'WSG';
      if (selectedFileType === 'reckweg' || selectedFileType === 'reck_stock') brandName = 'RECK';
      if (selectedFileType === 'bt' || selectedFileType === 'bt_stock') brandName = 'B&T';

      const response = await fetch(`${API_URL}/jnm-import/create-unmatched-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandName,
          categoryId: Number(createCategory),
          productNames: toCreate,
          company_id: Number(localStorage.getItem('company_id') || '4')
        })
      });

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.error || 'Failed to create unmatched products.');
      }

      setCreateSuccessMsg(`Successfully created ${resData.createdCount} new product(s) in Master Catalog! Applying stock & pricing now...`);
      // Remove created items from unmatched list
      setUnmatchedList(prev => prev.filter(item => !selectedUnmatched[item]));

      // Automatically re-process file to apply stock and pricing immediately
      setTimeout(() => {
        handleProceed();
      }, 500);

    } catch (err) {
      setError(err.message || 'Error creating unmatched products.');
    } finally {
      setIsCreatingUnmatched(false);
    }
  };

  const handleUploadOther = () => {
    setShowConfirmation(false);
    setUploadedFile(null);
    setExtractedData(null);
    setError(null);
    setShowRawData(false);
    setStockOption('current_retail');
    setUnmatchedList([]);
    setCreateSuccessMsg(null);
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setExtractedData(null);
    setError(null);
    setShowConfirmation(false);
    setShowRawData(false);
    setStockOption('current_retail');
    setUnmatchedList([]);
    setCreateSuccessMsg(null);
  };

  const getCardBorderColor = (typeId) => {
    if (selectedFileType !== typeId) return '';
    const colors = {
      wsi: 'border-primary',
      wsi_stock: 'border-primary',
      wsg: 'border-success',
      wsg_stock: 'border-success',
      reckweg: 'border-purple',
      reck_stock: 'border-purple',
      bt: 'border-info',
      bt_stock: 'border-info',
      other_products: 'border-info',
      jnu: 'border-warning',
      hsn_update: 'border-dark'
    };
    return colors[typeId] || 'border-primary';
  };

  const getCardBgColor = (typeId) => {
    if (selectedFileType !== typeId) return 'bg-white';
    const colors = {
      wsi: 'bg-primary text-white',
      wsi_stock: 'bg-primary text-white',
      wsg: 'bg-success text-white',
      wsg_stock: 'bg-success text-white',
      reckweg: 'bg-purple text-white',
      reck_stock: 'bg-purple text-white',
      bt: 'bg-info text-white',
      bt_stock: 'bg-info text-white',
      other_products: 'bg-info text-white',
      jnu: 'bg-warning text-dark',
      hsn_update: 'bg-dark text-white'
    };
    return colors[typeId] || 'bg-primary text-white';
  };

  const handleSelectType = (typeId) => {
    setSelectedFileType(typeId);
    resetUpload();
    if (typeId === 'bt' || typeId === 'bt_stock') {
      setCreateCategory('22'); // B&T Category
    } else if (typeId === 'reckweg' || typeId === 'reck_stock') {
      setCreateCategory('20'); // RN Category
    } else if (typeId === 'wsg' || typeId === 'wsg_stock') {
      setCreateCategory('1'); // Dilution(FM) / Specialities
    } else if (typeId === 'other_products') {
      setCreateCategory('23'); // Other Products
    } else {
      setCreateCategory('1');
    }
  };

  const masterFileTypes = fileTypes.filter(t => t.category === 'master');
  const stockFileTypes = fileTypes.filter(t => t.category === 'stock');
  const utilityFileTypes = fileTypes.filter(t => t.category === 'utility');

  return (
    <div className="min-vh-100 bg-light py-4">
      <div className="container">
        {/* Header */}
        <div className="mb-4">
          <h1 className="h2 fw-bold text-dark">Import Products</h1>
          <p className="text-muted">Import product data and stock updates from various Excel sources</p>
        </div>

        {/* Main Content Card */}
        <div className="card shadow-lg">
          <div className="card-body p-4 p-md-5">
            {!selectedFileType ? (
              <>
                <h2 className="h4 fw-semibold text-dark mb-2">
                  Select Import File Type
                </h2>
                <p className="text-muted mb-4">Choose whether you are importing product master files or updating stock & MRP prices</p>

                {/* Master File Imports Section */}
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                    <FileSpreadsheet className="text-primary" size={22} />
                    <h3 className="h5 fw-bold mb-0 text-dark">Master File Imports</h3>
                    <span className="badge bg-primary-subtle text-primary rounded-pill px-3">Product Creation</span>
                  </div>
                  <div className="row g-3">
                    {masterFileTypes.map((type) => (
                      <div key={type.id} className="col-6 col-md-4 col-lg-3">
                        <div
                          onClick={() => handleSelectType(type.id)}
                          className={`card h-100 cursor-pointer border-2 transition-all hover-shadow ${getCardBorderColor(type.id)} ${getCardBgColor(type.id)}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body text-center p-4">
                            <div className="mb-3">{type.icon}</div>
                            <h5 className="card-title fw-semibold mb-1">{type.name}</h5>
                            <p className={`card-text small ${selectedFileType === type.id ? 'text-white-50' : 'text-muted'}`}>
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stock & MRP Imports Section */}
                <div>
                  <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                    <BarChart3 className="text-success" size={22} />
                    <h3 className="h5 fw-bold mb-0 text-dark">Stock & MRP Imports</h3>
                    <span className="badge bg-success-subtle text-success rounded-pill px-3">Stock & Price Updates</span>
                  </div>
                  <div className="row g-3">
                    {stockFileTypes.map((type) => (
                      <div key={type.id} className="col-6 col-md-4 col-lg-3">
                        <div
                          onClick={() => handleSelectType(type.id)}
                          className={`card h-100 cursor-pointer border-2 transition-all hover-shadow ${getCardBorderColor(type.id)} ${getCardBgColor(type.id)}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body text-center p-4">
                            <div className="mb-3">{type.icon}</div>
                            <h5 className="card-title fw-semibold mb-1">{type.name}</h5>
                            <p className={`card-text small ${selectedFileType === type.id ? 'text-white-50' : 'text-muted'}`}>
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Utility Imports Section */}
                <div className="mt-5">
                  <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                    <FileText className="text-dark" size={22} />
                    <h3 className="h5 fw-bold mb-0 text-dark">Utility Imports</h3>
                    <span className="badge bg-dark-subtle text-dark rounded-pill px-3">HSN & Tax Updates</span>
                  </div>
                  <div className="row g-3">
                    {utilityFileTypes.map((type) => (
                      <div key={type.id} className="col-6 col-md-4 col-lg-3">
                        <div
                          onClick={() => handleSelectType(type.id)}
                          className={`card h-100 cursor-pointer border-2 transition-all hover-shadow ${getCardBorderColor(type.id)} ${getCardBgColor(type.id)}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body text-center p-4">
                            <div className="mb-3">{type.icon}</div>
                            <h5 className="card-title fw-semibold mb-1">{type.name}</h5>
                            <p className={`card-text small ${selectedFileType === type.id ? 'text-white-50' : 'text-muted'}`}>
                              {type.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Selected File Type Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-column flex-sm-row gap-3">
                  <div>
                    <h2 className="h4 fw-semibold text-dark mb-1">
                      Import {fileTypes.find(t => t.id === selectedFileType)?.name}
                    </h2>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {stockOption && (
                        <span className="badge bg-info text-white">
                          {stockOption === 'current_retail' ? 'Current Retail Stock' :
                           stockOption === 'current_wholesale' ? 'Current Wholesale Stock' :
                           'Current Stock'}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFileType(null);
                      resetUpload();
                    }}
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                  >
                    <X size={16} />
                    Change Type
                  </button>
                </div>

                {/* Stock Type Selection */}
                <div className="mb-4 px-3 py-3 rounded-4 border border-secondary-subtle bg-white">
                  <p className="text-muted small mb-2">Choose how this import should be applied:</p>
                  <div className="d-flex flex-wrap gap-2 mb-2" role="group">
                    <button
                      type="button"
                      onClick={() => setStockOption('current_retail')}
                      className={`btn btn-md ${stockOption === 'current_retail' ? 'btn-primary text-white' : 'btn-outline-primary'}`}
                    >
                      Current Retail Stock
                    </button>
                    <button
                      type="button"
                      onClick={() => setStockOption('current_wholesale')}
                      className={`btn btn-md ${stockOption === 'current_wholesale' ? 'btn-primary text-white' : 'btn-outline-primary'}`}
                    >
                      Current Wholesale Stock
                    </button>
                  </div>

                  {/* Category Selection for WSI & WSG stock import */}
                  {(selectedFileType === 'wsi_stock' || selectedFileType === 'wsi' || selectedFileType === 'wsg_stock' || selectedFileType === 'wsg') && (
                    <div className="mt-3 pt-3 border-top">
                      <label className="fw-semibold text-dark mb-1 small d-block">
                        Filter / Target Category for WSI & WSG Import:
                      </label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="form-select form-select-sm"
                        style={{ maxWidth: '350px' }}
                      >
                        <option value="all">All Categories (Auto Detect from File / Sheet)</option>
                        <option value="Dilution(FM)">Dilution(FM)</option>
                        <option value="Dilution(Others)">Dilution(Others)</option>
                        <option value="MT">MT (Mother Tincture)</option>
                        <option value="Biochemics">Biochemics</option>
                        <option value="Biocombinations">Biocombinations</option>
                        <option value="LATT">LATT</option>
                        <option value="Schwabe_Naturals">Schwabe Naturals</option>
                        <option value="SPECIALITIES">Specialities</option>
                      </select>
                      <p className="small text-muted mt-1 mb-0">
                        Select a specific category if your file contains products for only one category (e.g. Dilution(FM)), or choose Auto Detect for multi-sheet files.
                      </p>
                    </div>
                  )}

                  <p className="small text-muted mt-2 mb-0">
                    Current stock options update live retail or wholesale inventory quantities in stock variations.
                  </p>
                </div>

                {/* Upload Area */}
                <div className="row justify-content-center">
                  <div className="col-12 col-md-8 col-lg-6">
                    {/* Drag & Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`border border-3 border-dashed rounded-4 p-5 text-center transition-all ${isDragging ? 'border-primary bg-primary-subtle' : uploadedFile ? 'border-success bg-success-subtle' : 'border-secondary bg-light'}`}
                      style={{ minHeight: '280px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                    >
                      {!uploadedFile ? (
                        <>
                          <div className="mb-3">
                            <Upload size={64} className={isDragging ? 'text-primary' : 'text-secondary'} />
                          </div>
                          <h3 className="h5 fw-semibold text-secondary mb-2">
                            {isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
                          </h3>
                          <p className="text-muted mb-3">or</p>
                          <label className="btn btn-primary d-inline-flex align-items-center gap-2">
                            <FileText size={20} />
                            <span className='text-white'>Select File</span>
                            <input
                              type="file"
                              accept=".xlsx,.xls"
                              onChange={handleFileInputChange}
                              className="d-none"
                            />
                          </label>
                          <p className="small text-muted mt-3 mb-0">
                            Supported formats: .xlsx, .xls
                          </p>
                          {selectedFileType === 'wsi' && (
                            <p className="small text-muted mt-2 mb-0">
                              <em>Looking for sheets: Dilution(FM), Dilution(Others)</em>
                            </p>
                          )}
                        </>
                      ) : showConfirmation ? (
                        /* Confirmation Dialog */
                        <div className="text-center">
                          <div className="mb-3">
                            <FileSpreadsheet size={48} className="text-success" />
                          </div>
                          <h4 className="fw-semibold text-dark mb-2">{uploadedFile.name}</h4>
                          <p className="text-muted mb-4">
                            {(uploadedFile.size / 1024).toFixed(2)} KB
                          </p>
                          <p className="text-secondary mb-4">Do you want to proceed with this file?</p>
                          <div className="d-flex gap-3 justify-content-center">
                            <button
                              onClick={handleProceed}
                              className="btn btn-success d-flex align-items-center gap-2 px-4"
                            >
                              <CheckCircle size={18} />
                              Proceed
                            </button>
                            <button
                              onClick={handleUploadOther}
                              className="btn btn-outline-secondary d-flex align-items-center gap-2 px-4"
                            >
                              <Upload size={18} />
                              Upload Other
                            </button>
                          </div>
                        </div>
                      ) : extractedData ? (
                        /* File Selected - Show file info */
                        <div className="d-flex align-items-center justify-content-between bg-white p-3 rounded-3 shadow-sm">
                          <div className="d-flex align-items-center gap-3">
                            <FileSpreadsheet size={40} className="text-success" />
                            <div className="text-start">
                              <p className="fw-medium text-dark mb-1">{uploadedFile.name}</p>
                              <p className="small text-muted mb-0">
                                {(uploadedFile.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={resetUpload}
                            className="btn btn-light btn-sm rounded-circle p-2"
                          >
                            <X size={20} className="text-secondary" />
                          </button>
                        </div>
                      ) : (
                        /* Fallback */
                        <div className="d-flex align-items-center justify-content-between bg-white p-3 rounded-3 shadow-sm">
                          <div className="d-flex align-items-center gap-3">
                            <FileSpreadsheet size={40} className="text-success" />
                            <div className="text-start">
                              <p className="fw-medium text-dark mb-1">{uploadedFile.name}</p>
                              <p className="small text-muted mb-0">
                                {(uploadedFile.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={resetUpload}
                            className="btn btn-light btn-sm rounded-circle p-2"
                          >
                            <X size={20} className="text-secondary" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Processing State */}
                    {isProcessing && (
                      <div className="mt-4 text-center text-primary">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="fw-medium">Processing file...</span>
                      </div>
                    )}

                    {/* Error State */}
                    {error && (
                      <div className="mt-4 alert alert-danger d-flex align-items-start gap-2">
                        <AlertCircle size={20} className="flex-shrink-0 mt-1" />
                        <div>
                          <strong>Import Error</strong>
                          <p className="mb-0 small">{error}</p>
                          <button 
                            onClick={resetUpload}
                            className="btn btn-sm btn-outline-danger mt-2"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Success State - Data Preview */}
                    {extractedData && (
                      <div className="mt-4">
                        {/* API Response Preview */}
                        <div className="alert alert-success d-flex align-items-center gap-2">
                          <CheckCircle size={20} />
                          <span>File processed successfully via API</span>
                        </div>

                        {/* Sheets Summary */}
                        {extractedData.sheetsFound && (
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {Object.entries(extractedData.sheetsFound).map(([sheet, found]) => (
                              <span
                                key={sheet}
                                className={`badge ${found ? 'bg-success' : 'bg-secondary'}`}
                              >
                                {found ? '✓' : '✗'} {sheet}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Dilution(FM) Summary */}
                        {extractedData.DilutionFM?.products?.length > 0 && (
                          <div className="card mb-3 border-success">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center gap-2 text-success">
                                <CheckCircle size={20} />
                                Dilution(FM)
                              </h5>
                              <div className="row text-center mt-3">
                                <div className="col-6">
                                  <h4 className="mb-0">{extractedData.DilutionFM.products.length}</h4>
                                  <small className="text-muted">Products</small>
                                </div>
                                <div className="col-6">
                                  <h4 className="mb-0">{extractedData.DilutionFM.variations.length}</h4>
                                  <small className="text-muted">Variations</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Dilution(Others) Summary */}
                        {extractedData.DilutionOthers?.products?.length > 0 && (
                          <div className="card mb-3 border-success">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center gap-2 text-success">
                                <CheckCircle size={20} />
                                Dilution(Others)
                              </h5>
                              <div className="row text-center mt-3">
                                <div className="col-6">
                                  <h4 className="mb-0">{extractedData.DilutionOthers.products.length}</h4>
                                  <small className="text-muted">Products</small>
                                </div>
                                <div className="col-6">
                                  <h4 className="mb-0">{extractedData.DilutionOthers.variations.length}</h4>
                                  <small className="text-muted">Variations</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Other Products Summary */}
                        {extractedData.summary && selectedFileType === 'other_products' && (
                          <div className="card mb-3 border-info">
                            <div className="card-body">
                              <h5 className="card-title d-flex align-items-center gap-2 text-info">
                                <CheckCircle size={20} />
                                Other Products Import Results
                              </h5>
                              <div className="row text-center mt-3 g-2">
                                <div className="col-4">
                                  <h4 className="mb-0">{extractedData.summary.totalProducts || 0}</h4>
                                  <small className="text-muted">Unique Products</small>
                                </div>
                                <div className="col-4">
                                  <h4 className="mb-0">{extractedData.summary.totalVariations || 0}</h4>
                                  <small className="text-muted">Variations</small>
                                </div>
                                <div className="col-4">
                                  <h4 className="mb-0">{extractedData.summary.created || 0}</h4>
                                  <small className="text-muted">Created</small>
                                </div>
                                <div className="col-4">
                                  <h4 className="mb-0">{extractedData.summary.updated || 0}</h4>
                                  <small className="text-muted">Updated</small>
                                </div>
                                <div className="col-4">
                                  <h4 className="mb-0">{extractedData.summary.availableVariantsCreated || 0}</h4>
                                  <small className="text-muted">New Variants</small>
                                </div>
                                <div className="col-4">
                                  <h4 className="mb-0">{extractedData.summary.errors || 0}</h4>
                                  <small className="text-muted text-danger">Errors</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Reck Stock & MRP Summary */}
                        {extractedData.summary && selectedFileType === 'reck_stock' && (
                          <div className="mb-3">
                            <div className="card border-purple mb-3">
                              <div className="card-body">
                                <h5 className="card-title d-flex align-items-center gap-2 text-purple">
                                  <BarChart3 size={20} />
                                  Reckweg Stock & MRP Import Results
                                </h5>
                                <div className="row text-center mt-3 g-2">
                                  <div className="col-3">
                                    <h4 className="mb-0">{extractedData.summary.totalSheets || 0}</h4>
                                    <small className="text-muted">Sheets</small>
                                  </div>
                                  <div className="col-3">
                                    <h4 className="mb-0">{extractedData.summary.totalMatched || 0}</h4>
                                    <small className="text-muted">Matched</small>
                                  </div>
                                  <div className="col-3">
                                    <h4 className="mb-0">{extractedData.summary.totalCreated || 0}</h4>
                                    <small className="text-muted">Created</small>
                                  </div>
                                  <div className="col-3">
                                    <h4 className="mb-0">{extractedData.summary.totalUpdated || 0}</h4>
                                    <small className="text-muted">Updated</small>
                                  </div>
                                </div>
                                {extractedData.summary.totalUnmatched > 0 && (
                                  <div className="mt-2 text-center">
                                    <small className="text-warning">
                                      {extractedData.summary.totalUnmatched} medicine names could not be matched
                                    </small>
                                  </div>
                                )}
                                {extractedData.summary.totalErrors > 0 && (
                                  <div className="mt-1 text-center">
                                    <small className="text-danger">
                                      {extractedData.summary.totalErrors} errors occurred
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Per-sheet breakdown */}
                            {extractedData.sheets && Object.entries(extractedData.sheets).map(([key, sheet]) => (
                              !sheet.skipped && (
                                <div key={key} className={`card mb-2 ${sheet.success === false ? 'border-danger' : 'border-success'}`}>
                                  <div className="card-body py-2 px-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <h6 className={`mb-0 d-flex align-items-center gap-1 ${sheet.success === false ? 'text-danger' : 'text-success'}`}>
                                        {sheet.success === false ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
                                        {sheet.sheetName || key}
                                      </h6>
                                      <div className="d-flex gap-3">
                                        {sheet.matched != null && <small className="text-muted">{sheet.matched} matched</small>}
                                        {sheet.created != null && <small className="text-success">{sheet.created} created</small>}
                                        {sheet.updated != null && <small className="text-info">{sheet.updated} updated</small>}
                                      </div>
                                    </div>
                                    {sheet.error && <p className="text-danger small mb-0 mt-1">{sheet.error}</p>}
                                  </div>
                                </div>
                              )
                            ))}
                          </div>
                        )}

                        {/* WSG / Reck Sheets Summary */}
                        {extractedData.sheets && selectedFileType !== 'reck_stock' && (
                          <div className="mb-3">
                            {Object.entries(extractedData.sheets).map(([key, sheet]) => (
                              sheet.success && sheet.data?.summary ? (
                                <div key={key} className="card mb-2 border-success">
                                  <div className="card-body">
                                    <h5 className="card-title d-flex align-items-center gap-2 text-success">
                                      <CheckCircle size={20} />
                                      {sheet.sheetName}
                                    </h5>
                                    <div className="row text-center mt-3">
                                      <div className="col-3">
                                        <h4 className="mb-0">{sheet.data.summary.totalProducts || 0}</h4>
                                        <small className="text-muted">Products</small>
                                      </div>
                                      <div className="col-3">
                                        <h4 className="mb-0">{sheet.data.summary.totalVariations || 0}</h4>
                                        <small className="text-muted">Variations</small>
                                      </div>
                                      <div className="col-3">
                                        <h4 className="mb-0">{sheet.data.summary.created || 0}</h4>
                                        <small className="text-muted">Created</small>
                                      </div>
                                      <div className="col-3">
                                        <h4 className="mb-0">{sheet.data.summary.updated || 0}</h4>
                                        <small className="text-muted">Updated</small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : sheet.success === false ? (
                                <div key={key} className="card mb-2 border-danger">
                                  <div className="card-body">
                                    <h5 className="card-title d-flex align-items-center gap-2 text-danger">
                                      <AlertCircle size={20} />
                                      {sheet.sheetName}
                                    </h5>
                                    <p className="text-muted small mb-0">{sheet.error || 'Failed to process'}</p>
                                  </div>
                                </div>
                              ) : null
                            ))}
                          </div>
                        )}

                        {/* Unmatched Products Auto-Creation Panel */}
                        {unmatchedList && unmatchedList.length > 0 && (
                          <div className="card mb-4 border-warning shadow-sm">
                            <div className="card-header bg-warning-subtle text-dark fw-bold d-flex justify-content-between align-items-center">
                              <span className="d-flex align-items-center gap-2">
                                <AlertCircle size={20} className="text-warning" />
                                {unmatchedList.length} Product(s) Not Found in Catalog
                              </span>
                              <span className="badge bg-warning text-dark">Action Available</span>
                            </div>
                            <div className="card-body">
                              <p className="small text-muted mb-3">
                                The products listed below were found in your stock import file but do not exist in the Master Product Catalog. Choose a category below and select products to auto-create them:
                              </p>

                              {createSuccessMsg && (
                                <div className="alert alert-success d-flex align-items-center gap-2 mb-3 py-2 px-3 small">
                                  <CheckCircle size={18} />
                                  <span>{createSuccessMsg}</span>
                                </div>
                              )}

                              <div className="row g-3 mb-3 align-items-center">
                                <div className="col-12 col-md-6">
                                  <label className="fw-semibold small mb-1">Target Category for New Products:</label>
                                  <select
                                    value={createCategory}
                                    onChange={(e) => setCreateCategory(e.target.value)}
                                    className="form-select form-select-sm"
                                  >
                                    <option value="22">B&T</option>
                                    <option value="20">RN (Reckeweg Drops)</option>
                                    <option value="1">Dilution(FM)</option>
                                    <option value="2">Dilution(Others)</option>
                                    <option value="3">MT (Mother Tincture)</option>
                                    <option value="4">Biochemics</option>
                                    <option value="5">Biocombinations</option>
                                    <option value="16">LATT</option>
                                    <option value="14">Schwabe Naturals</option>
                                    <option value="17">Specialities</option>
                                    <option value="23">Other Products</option>
                                  </select>
                                </div>
                                <div className="col-12 col-md-6 text-end">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const allSelected = unmatchedList.every(i => selectedUnmatched[i]);
                                      const newSel = {};
                                      unmatchedList.forEach(i => { newSel[i] = !allSelected; });
                                      setSelectedUnmatched(newSel);
                                    }}
                                    className="btn btn-link btn-sm text-decoration-none p-0 me-3"
                                  >
                                    {unmatchedList.every(i => selectedUnmatched[i]) ? 'Deselect All' : 'Select All'}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isCreatingUnmatched || unmatchedList.filter(i => selectedUnmatched[i]).length === 0}
                                    onClick={handleCreateUnmatched}
                                    className="btn btn-warning btn-sm fw-semibold d-inline-flex align-items-center gap-1 text-dark"
                                  >
                                    {isCreatingUnmatched && <span className="spinner-border spinner-border-sm me-1" role="status" />}
                                    Create {unmatchedList.filter(i => selectedUnmatched[i]).length} Selected Product(s)
                                  </button>
                                </div>
                              </div>

                              <div className="border rounded p-2 bg-light" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                <div className="row g-2">
                                  {unmatchedList.map((item, idx) => (
                                    <div key={idx} className="col-12 col-sm-6 col-md-4">
                                      <div className="form-check form-check-inline text-truncate w-100 mb-1">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`unmatched-${idx}`}
                                          checked={!!selectedUnmatched[item]}
                                          onChange={(e) => setSelectedUnmatched(prev => ({ ...prev, [item]: e.target.checked }))}
                                        />
                                        <label className="form-check-label small cursor-pointer text-dark text-truncate" htmlFor={`unmatched-${idx}`} title={item}>
                                          {item}
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {createSuccessMsg && (
                                <div className="mt-3 text-end">
                                  <button
                                    type="button"
                                    onClick={handleProceed}
                                    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                                  >
                                    <Upload size={14} />
                                    Re-Process File Now to Apply Stock
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Done Button */}
                        <div className="d-flex gap-2 mt-4">
                          <button
                            onClick={resetUpload}
                            className="btn btn-primary flex-fill d-flex align-items-center justify-content-center gap-2"
                          >
                            <CheckCircle size={18} />
                            Done
                          </button>
                        </div>

                        {/* Raw Data Toggle */}
                        <div className="mt-3">
                          <button
                            onClick={() => setShowRawData(!showRawData)}
                            className="btn btn-link text-decoration-none p-0"
                          >
                            {showRawData ? 'Hide' : 'View'} Raw Data
                          </button>
                          
                          {showRawData && (
                            <pre className="mt-2 p-3 bg-dark text-success rounded-3 small" style={{ maxHeight: '300px', overflow: 'auto' }}>
                              {JSON.stringify(extractedData, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        .hover-shadow:hover {
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          transform: translateY(-2px);
          transition: all 0.2s ease;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .bg-purple {
          background-color: #6f42c1 !important;
        }
        .border-purple {
          border-color: #6f42c1 !important;
        }
        .text-purple {
          color: #6f42c1 !important;
        }
      `}</style>
    </div>
  );
};

export default ImportPage;